import { Injectable } from '@angular/core';

import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, debounceTime, filter, map, merge, mergeMap, of, switchMap, take, zip } from 'rxjs';

import { BetslipType } from '../../../core/betslip-type';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipBarVisibilityService } from '../../betslip-bar/services/betslip-bar-visibility.service';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector, selectIsLinearSingleBetBuilder } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { GroupLinearPickError, GroupPickError } from '../../validation/errors/general/group-pick-error';
import { IBetslipPickErrors } from '../../validation/state';
import BetslipPickActions from '../actions';
import { addGroup, removeAndReplaceGroup, removePicksFromGroup, replaceGroup, requestAddPickToGroup } from '../grouped-picks.actions';
import { betslipPicksListSelector, pickWithErrorSelector } from '../selectors';
import { IRemoveablePick, defaultLastPickErrorState } from '../state';

@Injectable()
export class LastPickErrorEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private betslipBarVisibility: BetslipBarVisibilityService,
    ) {}

    //Updates the last pick error state after removing a pick or toggling selection on betslip
    //if there is no error anymore set the error state to default otherwise set the pickId to undefined to prevent the Undo action
    handleRemoveOrTogglePickEffect$ = createEffect(() => {
        return merge(
            this.actions$.pipe(
                ofType(BetslipPickActions.requestRemovePick),
                concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
                // prevent this if the pick is part of a group. We have the removePicksFromGroup handler for that
                // the check is made based on the assumption that the picks that are grouped are not present in the pickList
                filter(([{ pickId }, picksList]) => picksList.some((pick) => pick.id.isEqual(pickId))),
                map(([action]) => action),
            ),
            this.actions$.pipe(
                ofType(BetslipPickActions.requestRemoveEntainUiBetBuilderPick, BetslipPickActions.togglePick, BetslipPickActions.removeMultiplePicks),
            ),
        ).pipe(
            concatLatestFrom(() => [this.betslipBarVisibility.enableDsl$, this.store.select(pickWithErrorSelector)]),
            filter(([_, betslipBarEnabled, lastPickState]) => betslipBarEnabled && this.isLastPickSet(lastPickState)),
            switchMap(() => this.getActions()),
            map(({ betslipErrors, pickErrors, betslipTypeErrors }) => this.getUndoStateAction({ betslipErrors, pickErrors, betslipTypeErrors })),
        );
    });

    handleRemovePickFromGroupEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removePicksFromGroup),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            map(([{ groupInfo }, pickList]) =>
                pickList.find((pick) => BaseBetslipGroupPick.isPick(pick) && pick.groupInfo.groupId === groupInfo.groupId),
            ),
            filter(isDefined),
            map((groupPick) => groupPick.id),
            concatLatestFrom(() => [this.betslipBarVisibility.enableDsl$, this.store.select(pickWithErrorSelector)]),
            filter(([_, betslipBarEnabled, lastPickState]) => betslipBarEnabled && this.isLastPickSet(lastPickState)),
            switchMap(([sourceGroupPickId]) =>
                this.actions$.pipe(
                    ofType(replaceGroup, removeAndReplaceGroup, BetslipPickActions.requestRemovePick),
                    map(({ pickId }) => pickId),
                    filter((pickId) => pickId.isEqual(sourceGroupPickId)),
                    take(1),
                    switchMap(() => this.getActions()),
                    map(({ betslipErrors, pickErrors, betslipTypeErrors }) =>
                        this.getUndoStateAction({ betslipErrors, pickErrors, betslipTypeErrors }),
                    ),
                ),
            ),
        );
    });

    //Updates the last pick error state after adding pick to betslip
    //if there was no error before set the pickId to the current pickId to enable the Undo action
    //if there was error before set the pickId to undefined to prevent the Undo action
    handleAddLastPickErrorEffect$ = createEffect(() => {
        return this.actions$.pipe(ofType(BetslipPickActions.requestAddPick, BetslipPickActions.requestAddEntainUiBetBuilderPick)).pipe(
            concatLatestFrom(() => this.betslipBarVisibility.enableDsl$),
            filter(([_, betslipbarEnabled]) => betslipbarEnabled),
            switchMap(([{ pickId }]) =>
                merge(
                    this.actions$.pipe(
                        ofType(requestAddPickToGroup),
                        filter((action) => action.pickId === pickId),

                        // seems like a lint bug
                        // eslint-disable-next-line @ngrx/avoid-cyclic-effects
                        switchMap((_) => this.actions$.pipe(ofType(replaceGroup))),
                    ),
                    this.actions$.pipe(
                        ofType(BetslipPickActions.replacePick),
                        filter((action) => action.pickId === pickId),
                        debounceTime(0),
                    ),
                ).pipe(
                    take(1),
                    switchMap(() => this.getActions()),
                    filter(({ betslipErrors, betslipTypeErrors, pickErrors }) => this.checkForErrors(pickErrors, betslipErrors, betslipTypeErrors)),
                    concatLatestFrom(() => [
                        this.store.select(pickWithErrorSelector),
                        this.store.select(betslipCurrentTypeSelector),
                        this.store.select(selectIsLinearSingleBetBuilder),
                    ]),
                    filter(
                        ([_, lastPickState, currentType, isLinearSingleBetBuilder]) => currentType === BetslipType.Combo || isLinearSingleBetBuilder,
                    ),
                    map(([_, lastPickState]) => {
                        if (lastPickState?.hasError && lastPickState.pickId) {
                            return of(BetslipPickActions.lastPickErrorRemovePickId());
                        } else if (!lastPickState?.hasError) {
                            return of(BetslipPickActions.addLastPickError({ pickId }));
                        }

                        return of();
                    }),
                    mergeMap((actions) => actions),
                ),
            ),
        );
    });

    //handling when precreated added the two scenarios are precreated added to a group which is already exist
    //precreated added when we have pick(s) in betslip and it wont be part of an existing group
    handleAddPrecreatedEffect$ = createEffect(() => {
        return this.actions$.pipe(ofType(BetslipPickActions.requestAddGroupPick)).pipe(
            concatLatestFrom(() => this.betslipBarVisibility.enableDsl$),
            filter(([_, betslipbarEnabled]) => betslipbarEnabled),
            switchMap((_) =>
                // seems like a lint bug
                // eslint-disable-next-line @ngrx/avoid-cyclic-effects
                merge(this.actions$.pipe(ofType(addGroup)), this.actions$.pipe(ofType(replaceGroup))).pipe(
                    take(1),
                    switchMap(() => this.getActions()),
                    map(({ betslipErrors, pickErrors, betslipTypeErrors }) =>
                        this.getUndoStateAction({ betslipErrors, pickErrors, betslipTypeErrors }),
                    ),
                ),
            ),
        );
    });

    //Updates the last pick error state after switching tabs on betslip
    //if there is no error anymore set the error state to default otherwise set the pickId to undefined to prevent the Undo action
    handleUserSwitchingTabEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => [this.betslipBarVisibility.enableDsl$, this.store.select(betslipCurrentTypeSelector)]),
            filter(([action, betslipbarEnabled, currentType]) => action.selectedByUser && betslipbarEnabled && currentType === BetslipType.Combo),
            concatLatestFrom(() => this.store.select(pickWithErrorSelector)),
            filter(([_, lastPickState]) => lastPickState !== defaultLastPickErrorState),
            switchMap(() => this.getActions()),
            map(({ betslipErrors, pickErrors, betslipTypeErrors }) => this.getUndoStateAction({ betslipErrors, pickErrors, betslipTypeErrors })),
        );
    });

    //Handles remove pick when user clicks on undo button
    handleRemovingLastPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removeLastPickFromPickList),
            concatLatestFrom(() => this.store.select(pickWithErrorSelector)),
            filter(([_, lastPickState]) => !!lastPickState && !!lastPickState?.pickId),
            map(([_, lastPickState]) => {
                return BetslipPickActions.requestRemovePick({ pickId: lastPickState.pickId! });
            }),
        );
    });

    private checkForErrors(pickErrors: IBetslipPickErrors, betslipErrors: BetslipError[], betslipTypeErrors: BetslipError[]): boolean {
        return (
            Object.values(pickErrors).some((resultError) =>
                resultError.some((e) => e.type === PlacementErrorType.ComboPrevention || e instanceof GroupLinearPickError),
            ) ||
            betslipErrors.some((e) => e instanceof GroupPickError) ||
            betslipTypeErrors.some((e) => e instanceof GroupPickError)
        );
    }

    private getActions(): Observable<{
        pickErrors: IBetslipPickErrors;
        betslipErrors: BetslipError[];
        betslipTypeErrors: BetslipError[];
    }> {
        const pickErrors$ = this.actions$.pipe(ofType(BetslipErrorsActions.picksErrorsCheckFinished));
        const betslipErrors$ = this.actions$.pipe(ofType(BetslipErrorsActions.betslipErrorsCheckFinished));

        const isLinearSingleBetBuilder$ = this.store.select(selectIsLinearSingleBetBuilder);

        return zip([pickErrors$, betslipErrors$]).pipe(
            concatLatestFrom(() => [isLinearSingleBetBuilder$]),
            map(([[pickErrors, betslipErrors], isLinearSingleBetBuilder]) => {
                const betslipType = isLinearSingleBetBuilder ? BetslipType.BetBuilder : BetslipType.Combo;

                return {
                    pickErrors: pickErrors.errors[betslipType],
                    betslipErrors: betslipErrors.errors,
                    betslipTypeErrors: betslipErrors.typeSlipErrors[betslipType],
                };
            }),
            take(1),
        );
    }

    private getUndoStateAction = ({
        pickErrors,
        betslipErrors,
        betslipTypeErrors,
    }: {
        pickErrors: IBetslipPickErrors;
        betslipErrors: BetslipError[];
        betslipTypeErrors: BetslipError[];
    }) =>
        !this.checkForErrors(pickErrors, betslipErrors, betslipTypeErrors)
            ? BetslipPickActions.removeLastPickError()
            : BetslipPickActions.lastPickErrorRemovePickId();

    private isLastPickSet(lastPickState: IRemoveablePick): boolean {
        return lastPickState !== defaultLastPickErrorState;
    }
}
